import React, { useState } from 'react';
import { Info, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import { canViewSql } from '@/constants/sqlViewerAccess';
import type { DiretoriaQuerySource } from '@/hooks/useDiretoriaSqlCatalog';

const SOURCE_LABEL: Record<DiretoriaQuerySource, string> = {
  erp: 'ERP FlyStart',
  cobranca: 'Cobrança (MySQL / Prisma)',
  oficina: 'Oficina (MySQL / Prisma)',
};

const SOURCE_HINT: Record<DiretoriaQuerySource, string> = {
  erp: 'Consulta executada no ERP FlyStart via API de SQL.',
  cobranca: 'Equivalente SQL documentado; dados obtidos via Prisma no endpoint /reports/dashboard.',
  oficina: 'Dados operacionais da oficina (espelho ERP, app mobile e agendamentos).',
};

export interface DiretoriaSqlButtonProps {
  sql?: string | null;
  source?: DiretoriaQuerySource;
  /** Título do modal (padrão: Consulta SQL). */
  title?: string;
  /** Explicação em linguagem clara do que compõe o indicador (visível a todos). */
  explanation?: string | null;
  className?: string;
  stopPropagation?: boolean;
}

export const DiretoriaSqlButton: React.FC<DiretoriaSqlButtonProps> = ({
  sql,
  source,
  title = 'Consulta SQL',
  explanation,
  className,
  stopPropagation = false,
}) => {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const showSql = canViewSql(user?.email) && Boolean(sql?.trim());
  const showExplanation = Boolean(explanation?.trim());
  if (!showSql && !showExplanation) return null;

  const handleCopy = async () => {
    if (!sql?.trim()) return;
    try {
      await navigator.clipboard.writeText(sql.trim());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          if (stopPropagation) {
            e.stopPropagation();
            e.preventDefault();
          }
          setOpen(true);
        }}
        className={cn(
          'inline-flex items-center justify-center rounded-full p-0.5',
          'text-muted-foreground hover:text-primary hover:bg-muted transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
        aria-label={showExplanation ? `Entender: ${title}` : `Ver consulta SQL: ${title}`}
        title={showExplanation ? 'Entender o indicador' : 'Ver consulta SQL'}
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={title} size="lg">
        <div className="space-y-4 text-left">
          {showExplanation ? (
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">O que é este indicador</h4>
              <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
                {explanation!.trim()}
              </p>
            </div>
          ) : null}

          {showSql && source ? (
            <div>
              <h4 className="mb-1 text-sm font-semibold text-foreground">Fonte</h4>
              <p className="text-sm leading-relaxed text-muted-foreground">
                <span className="mr-2 inline-flex rounded bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                  {SOURCE_LABEL[source]}
                </span>
                {SOURCE_HINT[source]}
              </p>
            </div>
          ) : null}

          {showSql ? (
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-foreground">SQL</h4>
                <Button type="button" variant="outline" size="sm" onClick={() => void handleCopy()}>
                  {copied ? (
                    <>
                      <Check className="mr-1 h-3.5 w-3.5" /> Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1 h-3.5 w-3.5" /> Copiar SQL
                    </>
                  )}
                </Button>
              </div>
              <pre className="max-h-[min(60vh,24rem)] overflow-x-auto overflow-y-auto whitespace-pre-wrap rounded-md bg-slate-900 p-3 font-mono text-xs text-slate-100 dark:bg-slate-950">
                {sql!.trim()}
              </pre>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

/** @deprecated Use DiretoriaSqlButton */
export const DiretoriaSqlTooltip = DiretoriaSqlButton;
